export const getVideoThumbnail = async (video: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(video);
    const videoElement = document.createElement('video');

    videoElement.src = url;
    videoElement.crossOrigin = 'anonymous';
    videoElement.preload = 'metadata';
    videoElement.muted = true;
    videoElement.playsInline = true;

    let resolved = false;

    const cleanupAndResolve = (dataURL: string) => {
      if (resolved) return;
      resolved = true;
      URL.revokeObjectURL(url);
      resolve(dataURL);
    };

    const cleanupAndReject = (error: Error) => {
      if (resolved) return;
      resolved = true;
      URL.revokeObjectURL(url);
      reject(error);
    };

    videoElement.onerror = () => {
      cleanupAndReject(new Error('Failed to load video'));
    };

    videoElement.onloadedmetadata = () => {
      const seekTime = Math.min(1, videoElement.duration || 0);
      setTimeout(() => {
        videoElement.currentTime = seekTime;
      }, 100);
    };

    videoElement.onseeked = () => {
      if (videoElement.videoWidth === 0 || videoElement.videoHeight === 0) {
        cleanupAndReject(new Error('Video dimensions not available'));
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        cleanupAndReject(new Error('Could not get canvas context'));
        return;
      }

      ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

      try {
        const dataURL = canvas.toDataURL('image/png');
        cleanupAndResolve(dataURL);
      } catch (error) {
        cleanupAndReject(new Error('Failed to generate thumbnail: ' + (error as Error)?.message));
      }
    };

    const timeoutId = setTimeout(() => {
      cleanupAndReject(new Error('Timeout while generating thumbnail'));
    }, 5000);

    const clearTimers = () => clearTimeout(timeoutId);

    const originalResolve = resolve;
    const originalReject = reject;
    resolve = (value) => {
      clearTimers();
      originalResolve(value);
    };
    reject = (error) => {
      clearTimers();
      originalReject(error);
    };
  });
};

export const getVideoThumbnailFromFileOrUrl = async (
  fileOrUrl: File | string,
  seekTime = 1,         // seconds – change at will
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const src = typeof fileOrUrl === 'string'
      ? fileOrUrl
      : URL.createObjectURL(fileOrUrl);

    video.src = src;
    video.crossOrigin = 'anonymous';
    video.preload     = 'metadata';
    video.muted       = true;
    video.playsInline = true;

    const revoke = () => {
      if (fileOrUrl instanceof File) URL.revokeObjectURL(src);
    };
    video.addEventListener('loadeddata', () => {
      video.currentTime = Math.min(seekTime, (video.duration || 0) / 2);
    });
    video.addEventListener('seeked', () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width  = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not get canvas context');

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);   // <-- MISSED LINE
        const dataURL = canvas.toDataURL('image/png');

        revoke();
        resolve(dataURL);
      } catch (err) {
        revoke();
        reject(err);
      }
    });

    video.addEventListener('error', () => {
      revoke();
      reject(new Error('Error loading video for thumbnail generation'));
    });
  });
};

export const getFileName = (item: Tributes.MediaFile): string =>
  item instanceof File ? item.name : (item.id ?? 'Media');

export const getFileType = (item: Tributes.MediaFile): string =>
  item instanceof File ? item.type : (item.mediaType ?? '');

export const isVideoFile = (item: Tributes.MediaFile): boolean =>
  getFileType(item).startsWith('video');

export const isImageFile = (item: Tributes.MediaFile): boolean =>
  getFileType(item).startsWith('image');
